'use client'

import { useState, useEffect } from 'react'
import { useSearchParams } from 'next/navigation'
import { X } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { createClientComponentClient } from '@supabase/auth-helpers-nextjs'
import { useRouter } from 'next/navigation'
import { toast } from 'react-hot-toast'
import { useAuth } from '@/contexts/AuthContext'

type AuthDrawerProps = {
  isOpen: boolean
  onClose: () => void
  defaultTab?: 'signin' | 'signup'
}

export function AuthDrawer({ isOpen, onClose, defaultTab = 'signin' }: AuthDrawerProps) {
  const [tab, setTab] = useState<string>(defaultTab)
  const [isLoading, setIsLoading] = useState(false)
  const router = useRouter()
  const searchParams = useSearchParams()
  const redirectPath = searchParams.get('redirect')
  const supabase = createClientComponentClient()
  const { signIn, user } = useAuth() // Get user and signIn from auth context
  
  // Disable auth check to stop redirect loops
  // useEffect(() => {
  //   const checkAlreadyAuthenticated = async () => {
  //     if (!isOpen) return;
      
  //     try {
  //       // First try context
  //       if (user) {
  //         console.log('User already authenticated via context, redirecting to dashboard');
  //         onClose();
  //         router.push(redirectPath || '/dashboard');
  //         return;
  //       }

  //       // Then try localStorage and cookies
  //       const hasLocalAuthFlag = 
  //         localStorage.getItem('force_auth_success') === 'true' || 
  //         localStorage.getItem('auth_session_active') === 'true';
        
  //       const hasCookieAuthFlag = 
  //         document.cookie.includes('auth_state=authenticated') || 
  //         document.cookie.includes('supabase-auth-token');
          
  //       if (hasLocalAuthFlag || hasCookieAuthFlag) {
  //         console.log('Auth flags detected, redirecting to dashboard');
  //         onClose();
  //         router.push(redirectPath || '/dashboard');
  //         return;
  //       }
        
  //       // Finally, check with Supabase directly
  //       const { data } = await supabase.auth.getSession();
  //       if (data.session) {
  //         console.log('Session detected, redirecting to dashboard');
  //         // Set auth flags for future checks
  //         localStorage.setItem('force_auth_success', 'true');
  //         localStorage.setItem('auth_session_active', 'true');
  //         onClose();
  //         router.push(redirectPath || '/dashboard');
  //       }
  //     } catch (error) {
  //       console.error('Error checking authentication status:', error);
  //     }
  //   };
    
  //   checkAlreadyAuthenticated();
  // }, [isOpen, user, router, redirectPath, onClose, supabase.auth]);
  
  // Sync internal tab state with defaultTab prop
  useEffect(() => {
    if (isOpen) { // Only update when drawer is open or opening
      setTab(defaultTab);
    }
  }, [defaultTab, isOpen]);
  
  // Form states
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [rememberMe, setRememberMe] = useState(false)
  
  // Handle sign in with email/password
  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!email || !password) {
      toast.error('Please enter both email and password')
      return
    }
    
    try {
      setIsLoading(true)
      
      // First, try to clear any existing sessions to avoid conflicts
      // await supabase.auth.signOut() // REMOVED THIS LINE
      
      // Check for cookie support
      document.cookie = "testcookie=1; SameSite=Strict; Secure; path=/";
      if (!document.cookie.includes("testcookie=")) {
        console.warn("Cookies may be blocked by the browser - this can cause auth issues");
      }
      
      // Set all flags BEFORE attempting login to ensure they're available
      if (typeof window !== 'undefined') {
        // Set flags in both localStorage and cookies for redundancy
        localStorage.setItem('rememberMe', rememberMe.toString());
        localStorage.setItem('auth_in_progress', 'true');
        sessionStorage.setItem('force_dashboard_view', 'true');
        localStorage.setItem('force_auth_success', 'true');
        
        // Also set cookies that the server can read
        document.cookie = `force_auth_success=true; path=/; max-age=${60*60}; SameSite=Lax`;
        document.cookie = `force_dashboard_view=true; path=/; max-age=${30*60}; SameSite=Lax`;
        
        // Store the redirect path if available
        if (redirectPath) {
          sessionStorage.setItem('auth_redirect_path', redirectPath);
          document.cookie = `auth_redirect_path=${redirectPath}; path=/; max-age=3600; SameSite=Lax`;
        }
      }
      
      // Use direct Supabase auth call to avoid potential context issues
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        throw error
      }
      
      // Success path - user is authenticated
      console.log('Authentication successful, preparing redirect...');
      
      // Close drawer first to prevent UI issues during redirect
      onClose()
      
      // Determine where to redirect
      let redirectTo = '/dashboard';
      
      // If there's a redirect path, use it instead of the default dashboard
      if (redirectPath && redirectPath.startsWith('/') && !redirectPath.startsWith('/auth')) {
        redirectTo = redirectPath;
      }
      
      // Force a hard browser navigation to the redirect destination with auth flags
      console.log('Direct navigation to:', redirectTo);
      
      // --- REMOVED FORM SUBMISSION LOGIC ---
      // const form = document.createElement('form');
      // form.method = 'POST';
      // form.action = redirectTo;
      // form.style.display = 'none';
      
      // // Add auth token info as hidden fields
      // const tokenField = document.createElement('input');
      // tokenField.type = 'hidden';
      // tokenField.name = 'auth_success';
      // tokenField.value = 'true';
      // form.appendChild(tokenField);
      
      // document.body.appendChild(form);
      
      // // Submit immediately
      // console.log('Submitting direct navigation form...');
      
      // // Wait a moment to ensure cookies are set
      // setTimeout(() => {
      //   // As a fallback, just use window.location if the form approach doesn't work
      //   window.location.href = redirectTo;
      // }, 100);

      // --- ADDED SIMPLE REDIRECT ---
      window.location.href = redirectTo;
      
      toast.success('Successfully signed in!');
    } catch (error: any) {
      console.error('AuthDrawer: Sign-in error:', error)
      toast.error(error.message || 'Error signing in')
      
      // Clear any partial auth state
      localStorage.removeItem('auth_in_progress')
      localStorage.removeItem('bypass_auth_check')
      sessionStorage.removeItem('auth_bypass')
      sessionStorage.removeItem('force_dashboard_view')
      
      // Also clear cookies
      document.cookie = 'force_auth_success=; path=/; max-age=0';
      document.cookie = 'force_dashboard_view=; path=/; max-age=0';
    } finally {
      setIsLoading(false)
    }
  }
  
  // Handle sign up with email/password
  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!name || !email || !password) {
      toast.error('Please fill in all fields')
      return
    }
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters')
      return
    }
    
    try {
      setIsLoading(true)
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: name,
          },
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      })
      
      if (error) {
        throw error
      }
      
      toast.success('Check your email to confirm your account!')
      onClose()
    } catch (error: any) {
      toast.error(error.message || 'Error signing up')
    } finally {
      setIsLoading(false)
    }
  }
  
  // Handle sign in with Google
  const handleGoogleSignIn = async () => {
    try {
      setIsLoading(true)
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      })
      
      if (error) {
        throw error
      }
      
      // OAuth redirects happen automatically, no need to do anything here
    } catch (error: any) {
      toast.error(error.message || 'Error signing in with Google')
      setIsLoading(false)
    }
  }
  
  // Handle sign in with GitHub
  const handleGithubSignIn = async () => {
    try {
      setIsLoading(true)
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'github',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      })
      
      if (error) {
        throw error
      }
      
      // OAuth redirects happen automatically, no need to do anything here
    } catch (error: any) {
      toast.error(error.message || 'Error signing in with GitHub')
      setIsLoading(false)
    }
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
            onClick={onClose}
          />
          
          {/* Drawer */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed right-0 top-0 z-50 h-full w-full max-w-md overflow-auto bg-[#1E1E2E] p-6 shadow-xl text-white"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">
                {tab === 'signin' ? 'Welcome Back' : 'Create Account'}
              </h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="rounded-full text-gray-300 hover:text-white hover:bg-gray-800"
              >
                <X className="h-5 w-5" />
                <span className="sr-only">Close</span>
              </Button>
            </div>
            
            <Tabs
              defaultValue={defaultTab}
              value={tab}
              onValueChange={setTab}
              className="w-full"
            >
              <TabsList className="grid w-full grid-cols-2 mb-6 bg-[#2D2D3A]">
                <TabsTrigger value="signin" className="data-[state=active]:bg-[#3B3B4F] data-[state=active]:text-white">Sign In</TabsTrigger>
                <TabsTrigger value="signup" className="data-[state=active]:bg-[#3B3B4F] data-[state=active]:text-white">Sign Up</TabsTrigger>
              </TabsList>
              
              <TabsContent value="signin" className="space-y-6">
                <form onSubmit={handleSignIn} className="space-y-4">
                  <div className="space-y-2">
                    <label htmlFor="signin-email" className="text-sm font-medium text-gray-200">
                      Email
                    </label>
                    <Input
                      id="signin-email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="bg-[#2D2D3A] border-[#3B3B4F] text-white placeholder-gray-400"
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label htmlFor="signin-password" className="text-sm font-medium text-gray-200">
                        Password
                      </label>
                      <a href="#" className="text-xs text-gray-400 hover:text-[#8A6FE8]">
                        Forgot password?
                      </a>
                    </div>
                    <Input
                      id="signin-password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="bg-[#2D2D3A] border-[#3B3B4F] text-white placeholder-gray-400"
                    />
                  </div>
                  <div className="flex items-center space-x-2 mb-2">
                    <input
                      type="checkbox"
                      id="remember-me"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-600 bg-[#2D2D3A] text-[#8A6FE8] focus:ring-[#8A6FE8]"
                    />
                    <label htmlFor="remember-me" className="text-sm text-gray-300">
                      Remember me
                    </label>
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-[#8A6FE8] hover:bg-[#7A5CD4] text-white"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Signing in...' : 'Sign In'}
                  </Button>
                </form>
                
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-gray-700" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-[#1E1E2E] px-2 text-gray-400">
                      Or continue with
                    </span>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <Button
                    variant="outline"
                    onClick={handleGoogleSignIn}
                    disabled={isLoading}
                    className="bg-[#2D2D3A] border-[#3B3B4F] text-white hover:bg-[#3B3B4F]"
                  >
                    Google
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleGithubSignIn}
                    disabled={isLoading}
                    className="bg-[#2D2D3A] border-[#3B3B4F] text-white hover:bg-[#3B3B4F]"
                  >
                    GitHub
                  </Button>
                </div>
              </TabsContent>
              
              <TabsContent value="signup" className="space-y-6">
                <form onSubmit={handleSignUp} className="space-y-4">
                  <div className="space-y-2">
                    <label htmlFor="signup-name" className="text-sm font-medium text-gray-200">
                      Name
                    </label>
                    <Input
                      id="signup-name"
                      type="text"
                      placeholder="Your Name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      className="bg-[#2D2D3A] border-[#3B3B4F] text-white placeholder-gray-400"
                    />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="signup-email" className="text-sm font-medium text-gray-200">
                      Email
                    </label>
                    <Input
                      id="signup-email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="bg-[#2D2D3A] border-[#3B3B4F] text-white placeholder-gray-400"
                    />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="signup-password" className="text-sm font-medium text-gray-200">
                      Password
                    </label>
                    <Input
                      id="signup-password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="bg-[#2D2D3A] border-[#3B3B4F] text-white placeholder-gray-400"
                    />
                    <p className="text-xs text-gray-400">
                      Must be at least 6 characters
                    </p>
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-[#8A6FE8] hover:bg-[#7A5CD4] text-white"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Signing up...' : 'Sign Up'}
                  </Button>
                </form>
                
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-gray-700" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-[#1E1E2E] px-2 text-gray-400">
                      Or continue with
                    </span>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <Button
                    variant="outline"
                    onClick={handleGoogleSignIn}
                    disabled={isLoading}
                    className="bg-[#2D2D3A] border-[#3B3B4F] text-white hover:bg-[#3B3B4F]"
                  >
                    Google
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleGithubSignIn}
                    disabled={isLoading}
                    className="bg-[#2D2D3A] border-[#3B3B4F] text-white hover:bg-[#3B3B4F]"
                  >
                    GitHub
                  </Button>
                </div>
              </TabsContent>
            </Tabs>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}